import React, { useMemo } from 'react';
import { ArrowUpRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  createMarkdownComponents,
  type MarkdownComponentsOptions,
} from '../utils/markdown/components';

export interface UseMarkdownComponentsOptions
  extends MarkdownComponentsOptions {
  /**
   * Enable timestamp link conversion (for video chat)
   * Converts patterns like (1:23) or [2:45] to clickable timestamps
   */
  enableTimestamps?: boolean;

  /**
   * Whether to hide timestamp indicators visually
   */
  hideTimestamps?: boolean;

  /**
   * Custom timestamp regex pattern
   * @default /[[(]?\s*(?:(?:\d+:)?\d{2}|\d):\d{2}\s*[)\]]?/g
   */
  timestampPattern?: RegExp;
}

/**
 * Hook for creating markdown components with optional timestamp support
 *
 * Combines the base markdown components with timestamp conversion logic
 * used by SelectedVideoChat
 */
export const useMarkdownComponents = (
  options: UseMarkdownComponentsOptions = {},
) => {
  const {
    enableTimestamps = false,
    hideTimestamps = false,
    timestampPattern = /[[(]?\s*(?:(?:\d+:)?\d{2}|\d):\d{2}\s*[)\]]?/g,
    onTimestampClick,
    ...baseOptions
  } = options;

  const components = useMemo(() => {
    // Start with base markdown components
    const baseComponents = createMarkdownComponents({
      ...baseOptions,
      onTimestampClick,
    });

    // If timestamps not enabled, return base components
    if (!enableTimestamps) {
      return baseComponents;
    }

    // Helper to convert timestamp string to seconds
    const parseTimestampToSeconds = (match: string): number => {
      const cleanMatch = match.replace(/[[\]()\s]/g, '');
      const timeParts = cleanMatch.split(':').map(Number);

      if (timeParts.length === 3) {
        const [hours, minutes, secs] = timeParts;
        return hours * 3600 + minutes * 60 + secs;
      }
      if (timeParts.length === 2) {
        const [minutes, secs] = timeParts;
        return minutes * 60 + secs;
      }
      return timeParts[0];
    };

    // Helper to get full timestamp string for title
    const getFullTimestamp = (timeParts: number[]) => {
      if (timeParts.length === 3) {
        return `${timeParts[0]}h ${timeParts[1]}m ${timeParts[2]}s`;
      }
      if (timeParts.length === 2) {
        return `${timeParts[0]}m ${timeParts[1]}s`;
      }
      return `${timeParts[0]}s`;
    };

    // Helper to convert timestamps in text to clickable links
    const convertTimestampsToLinks = (text: string) => {
      // If hiding timestamps, remove them
      if (hideTimestamps) {
        return text
          .replace(timestampPattern, ' ')
          .replace(/\s{3,}/g, ' ')
          .trim();
      }

      const parts = text.split(timestampPattern);
      const matches = text.match(timestampPattern);

      if (!matches) return text;

      return parts.flatMap((part, index) => {
        if (index < matches.length) {
          const match = matches[index];
          const seconds = parseTimestampToSeconds(match);
          const cleanMatch = match.replace(/[[\]()\s]/g, '');
          const timeParts = cleanMatch.split(':').map(Number);
          const fullTimestamp = getFullTimestamp(timeParts);

          // Skip rendering if surrounding parts are empty
          if (part.trim() === '' && parts[index + 1]?.trim() === '') {
            return [part];
          }

          return [
            part,
            onTimestampClick ? (
              <button
                key={`ts-${index}`}
                type="button"
                onClick={() => onTimestampClick(seconds)}
                className={cn(
                  'inline cursor-pointer align-middle transition-all duration-150 hover:scale-110 border-0 bg-transparent p-0',
                )}
                title={`Jump to ${fullTimestamp}`}
              >
                <ArrowUpRight
                  size={18}
                  className={cn(
                    'relative -top-0.5 inline-block px-[2px] align-middle transition-colors duration-150 text-gray-400 hover:text-primary',
                  )}
                  strokeWidth={1.5}
                />
              </button>
            ) : (
              match
            ),
          ];
        }
        return [part];
      });
    };

    // Process markdown children to convert timestamps
    const processMarkdownChildren = (children: React.ReactNode) => {
      if (Array.isArray(children)) {
        return children.map((child) => {
          if (typeof child === 'string') {
            return convertTimestampsToLinks(child);
          }
          return child;
        });
      }
      if (typeof children === 'string') {
        return convertTimestampsToLinks(children);
      }
      return children;
    };

    // Process timestamps without wrapping (for list items)
    const processTimestamps = (children: React.ReactNode): React.ReactNode => {
      if (typeof children === 'string') {
        return convertTimestampsToLinks(children);
      }
      if (Array.isArray(children)) {
        return children.map((child) => {
          if (typeof child === 'string') {
            return convertTimestampsToLinks(child);
          }
          return child;
        });
      }
      return children;
    };

    // Extend base components with timestamp processing
    return {
      ...baseComponents,
      p: ({ children }: { children?: React.ReactNode }) => (
        <p className="break-words whitespace-pre-wrap mb-3 last:mb-0 text-sm leading-normal">
          {processMarkdownChildren(children)}
        </p>
      ),
      ul: ({ children }: { children?: React.ReactNode }) => (
        <ul className="my-2 pl-6 list-disc">{children}</ul>
      ),
      ol: ({ children }: { children?: React.ReactNode }) => (
        <ol className="my-2 pl-6 list-decimal">{children}</ol>
      ),
      li: ({ children }: { children?: React.ReactNode }) => (
        <li className="text-sm leading-normal my-1.5">
          {processTimestamps(children)}
        </li>
      ),
      code: ({
        children,
        className,
      }: {
        children?: React.ReactNode;
        className?: string;
      }) => {
        const isInline = !className?.includes('language-');
        return isInline ? (
          <code className="bg-secondary/20 px-1.5 py-0.5 rounded text-xs font-mono">
            {children}
          </code>
        ) : (
          <code className={className}>{children}</code>
        );
      },
      pre: ({ children }: { children?: React.ReactNode }) => (
        <pre className="bg-secondary/20 p-4 my-3 rounded-md overflow-auto">
          <code className="bg-transparent p-0 text-xs leading-6 font-mono">
            {children}
          </code>
        </pre>
      ),
    };
  }, [
    enableTimestamps,
    hideTimestamps,
    timestampPattern,
    onTimestampClick,
    baseOptions,
  ]);

  return components;
};
